/**
 * useOrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguages
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbPostMachinesWithTraitsPart1VisionARPostView as OrbPostMachinesWithTraitsPart1VisionARPostViewEntity, OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesState =
  | 'idle';

export type OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent =
  | 'INIT';

type OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot = 'main';
interface OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesInternalState {
  machineState: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent | null;
  slotWriter: Partial<Record<OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot, OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent | null>>;
  loading: boolean;
  pendingEvent: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbPostMachinesWithTraitsPart1VisionARPostViewEntity>;
}

type OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesAction =
  | { type: 'EVENT_SUCCESS'; event: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot[]; renderClear?: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbPostMachinesWithTraitsPart1VisionARPostViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesReducer(
  state: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesInternalState,
  action: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesAction
): OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbPostMachinesWithTraitsPart1VisionARPostViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbPostMachinesWithTraitsPart1VisionARPostViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbPostMachinesWithTraitsPart1VisionARPostViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  currentLanguage: string;
  languages: { code: string; href: string; label: string }[];
  pickerContent: { className: Array<Array<number | string | string[]> | string>; items: Array<Array<string | { href: string[]; icon: Array<Array<string | string[]> | string>; id: string[]; label: string[] }> | string>; position: string; trigger: { icon: string; label: string[]; size: string; type: string; variant: string }; type: string };
}

const DEFAULT_ORB_POST_MACHINES_WITH_TRAITS_PART1_VISION_AR_SITE_HEADER_LANGUAGES_CONFIG = {
  currentLanguage: "@locale",
  languages: [{ code: "en", href: "/blog/machines-with-traits-part-1-vision", label: "English" }, { code: "ar", href: "/ar/blog/machines-with-traits-part-1-vision", label: "العربية" }, { code: "sl", href: "/sl/blog/machines-with-traits-part-1-vision", label: "Slovenščina" }],
  pickerContent: { className: ["if", ["==", ["array/len", "@config.languages"], 0], "hidden", ""], items: ["array/map", "@config.languages", ["fn", "entry", { href: ["object/get", "@entry", "href", ""], icon: ["if", ["==", ["object/get", "@entry", "code", ""], "@config.currentLanguage"], "check", ""], id: ["object/get", "@entry", "code", ""], label: ["object/get", "@entry", "label", ""] }]], position: "bottom-end", trigger: { icon: "globe", label: ["str/upper", "@config.currentLanguage"], size: "sm", type: "button", variant: "ghost" }, type: "menu" },
} as OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesConfig;

export interface OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesLogicReturn {
  state: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent | null;
  slotWriter: Partial<Record<OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot, OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent | null>>;
  loading: boolean;
  pendingEvent: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbPostMachinesWithTraitsPart1VisionARPostViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEventPayloadMap]: (payload?: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesConfig;
}

export function useOrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesLogic(config?: Partial<OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesConfig>): OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesLogicReturn {
  const mergedConfig: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesConfig = { ...DEFAULT_ORB_POST_MACHINES_WITH_TRAITS_PART1_VISION_AR_SITE_HEADER_LANGUAGES_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-post-machines-with-traits-part1-vision-ar-site-header-languages', orbitalName: 'OrbPostMachinesWithTraitsPart1VisionAR', traitName: 'OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguages' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbPostMachinesWithTraitsPart1VisionARPostView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot[]; clear: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot[], clear: [] as OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbPostMachinesWithTraitsPart1VisionARPostView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguages': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEventPayloadMap]: (payload?: OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguagesEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'OrbPostMachinesWithTraitsPart1VisionAR.OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguages', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguages', (): TraitStateSnapshot => ({
      traitName: 'OrbPostMachinesWithTraitsPart1VisionARSiteHeaderLanguages',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
